// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { illiquidsApi } from '@shared/data-access/api/engine/illiquids-api';
import { type RootIlliquidAssetsState } from '@shared/data-access/interfaces/redux';
import { selectClientIlliquidAssetsData } from '@shared/data-access/slices/illiquid-assets';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';
import { appSelect } from '@shared/ui-kit';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export interface UseIlliquidAssetsProps {
  clientId?: number | null;
  currency: AccountCurrency;
  skip?: boolean;
}

export const useIlliquidAssets = ({
  clientId,
  currency,
  skip = false,
}: UseIlliquidAssetsProps) => {
  const listIlliquidParams = useMemo(
    () => ({
      client_id: appSelect({
        advisor: clientId,
        client: undefined,
      }),
      currency,
    }),
    [clientId, currency],
  );

  const illiquidAssetsQuery = illiquidsApi.useListIlliquidAssetsQuery(
    listIlliquidParams,
    { skip },
  );

  const selectorClientId = appSelect({
    advisor: clientId!,
    client: 0,
  });

  const illiquidAssets = useSelector((state: RootIlliquidAssetsState) =>
    selectClientIlliquidAssetsData(state, selectorClientId),
  );

  return {
    illiquidAssets,
    illiquidAssetsQuery,
    listIlliquidParams,
  };
};
